import { AllocationPayload, VirtualWarehouseItem, getItemKey } from "@api/allocateManagementApi";
import throughShipmentApi, {
  RetailList,
  RetailVsCodeFilter,
  regionTypeName,
} from "@api/throughShipment/throughShipmentApi";
import {
  AllocationChannelOption,
  AllocationDestinationType,
  TRYALL_STOCK_CHANNEL_ID,
  TRYALL_STOCK_CHANNEL_OPTION,
  buildAllocationChannelOptions,
  buildRetailOptions,
} from "@constant/AllocationChannel";
import {
  clearDestinationData,
  fetchDestinationAllocatableQty,
  submitBatchAllocation,
} from "@redux/sharing/virtualWarehouseSlice";
import { useAppDispatch, useSelector } from "src/store";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Alert, Button, InputNumber, Modal, Select, Table, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const ConflictRow = styled.tr`
  outline: 2px solid #ff4d4f;
  outline-offset: -1px;
`;

const RedAlert = styled(Alert)`
  .ant-alert-message {
    color: red !important;
    font-size: 18px;
    text-align: center;
  }
`;

interface Props {
  selectedItems: VirtualWarehouseItem[];
  sourceDealerInput: AllocationChannelOption;
  onClose: () => void;
  onSuccess: () => void;
}

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-row-key"?: string;
}

interface AllocateRowContextValue {
  selectedItems: VirtualWarehouseItem[];
  conflictDealerNames: Set<string>;
}

const AllocateRowContext = React.createContext<AllocateRowContextValue>({
  selectedItems: [],
  conflictDealerNames: new Set(),
});

function AllocateTableRow({ className, ...rest }: TableRowProps) {
  const { selectedItems, conflictDealerNames } = React.useContext(AllocateRowContext);
  const rowKey = rest["data-row-key"];
  const item = selectedItems.find((i) => getItemKey(i) === rowKey);
  const isConflict = item?.retailerName !== null && conflictDealerNames.has(item?.retailerName ?? "");
  const RowComponent = isConflict ? ConflictRow : "tr";
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <RowComponent className={className} {...rest} />;
}

const TABLE_COMPONENTS = { body: { row: AllocateTableRow } };

export default function BatchAllocationModal({ selectedItems, sourceDealerInput, onClose, onSuccess }: Props) {
  const dispatch = useAppDispatch();
  const { isFetchingSubmit, destinationAllocatableMap, isFetchingDestination } = useSelector(
    (state) => state.virtualWarehouseSlice,
  );

  const [allocationTarget, setAllocationTarget] = useState<AllocationDestinationType | undefined>(undefined);
  const [retailerId, setRetailerId] = useState<number | undefined>(undefined);
  const [retailerName, setRetailerName] = useState<string>("");
  const [qtyInputs, setQtyInputs] = useState<Record<string, number | undefined>>({});
  const [retailList, setRetailList] = useState<RetailList[]>([]);

  useEffect(() => {
    return () => {
      dispatch(clearDestinationData());
    };
  }, [dispatch]);

  useEffect(() => {
    if (allocationTarget === undefined || allocationTarget === TRYALL_STOCK_CHANNEL_ID) return;
    throughShipmentApi
      .fetchRetailList({ regionTypes: allocationTarget, isVs: RetailVsCodeFilter.HAS_VS_CODE })
      .then((res) => setRetailList(res.results))
      .catch(() => {});
  }, [allocationTarget]);

  useEffect(() => {
    if (retailerId === undefined) return;
    dispatch(fetchDestinationAllocatableQty({ retailerId, sku: selectedItems.map((i) => i.sku) }));
  }, [retailerId, dispatch, selectedItems]);

  const allocationChannelOptions = useMemo(
    () => buildAllocationChannelOptions(sourceDealerInput.kind),
    [sourceDealerInput.kind],
  );

  const retailOptions = useMemo(() => buildRetailOptions(retailList), [retailList]);

  const handleAllocationChannelTypeChange = (value: AllocationDestinationType) => {
    setAllocationTarget(value);
    setRetailerId(undefined);
    setRetailerName("");
    setRetailList([]);
    if (value === TRYALL_STOCK_CHANNEL_ID) {
      setRetailerId(TRYALL_STOCK_CHANNEL_ID);
      setRetailerName(TRYALL_STOCK_CHANNEL_OPTION.label);
    }
  };

  const handleDealerChange = (value: number) => {
    const found = retailList.find((r) => r.id === value);
    setRetailerId(value);
    setRetailerName(found?.name ?? "");
  };

  const destinationDisplayText = useMemo(() => {
    if (allocationTarget === undefined) return "請選擇通路類型";
    if (allocationTarget === TRYALL_STOCK_CHANNEL_ID) return TRYALL_STOCK_CHANNEL_OPTION.label;
    if (!retailerId) return regionTypeName[allocationTarget] ?? "請選擇通路類型";
    return `${regionTypeName[allocationTarget]}_${retailerName}`;
  }, [allocationTarget, retailerId, retailerName]);

  const conflictDealerNames = useMemo(() => {
    if (!retailerName) return new Set<string>();
    return new Set(selectedItems.filter((i) => i.retailerName === retailerName).map(() => retailerName));
  }, [retailerName, selectedItems]);

  const hasConflict = conflictDealerNames.size > 0;

  const allocateRowContextValue = useMemo(
    () => ({ selectedItems, conflictDealerNames }),
    [selectedItems, conflictDealerNames],
  );

  const isConfirmDisabled =
    retailerId === undefined ||
    selectedItems.every((item) => !qtyInputs[getItemKey(item)]) ||
    selectedItems.some((item) => {
      const qty = qtyInputs[getItemKey(item)];
      return qty !== undefined && qty > item.canUseQty;
    }) ||
    hasConflict;

  const handleConfirm = () => {
    if (retailerId === undefined) return;
    const payloads = selectedItems
      .map((item): AllocationPayload | null => {
        const qty = qtyInputs[getItemKey(item)];
        if (!qty) return null;
        if (item.alloId !== undefined) return { alloId: item.alloId, destRetailerId: retailerId, qty };
        if (item.stockId != null) return { stockId: item.stockId, destRetailerId: retailerId, qty };
        return null;
      })
      .filter((p): p is AllocationPayload => p !== null);
    if (payloads.length === 0) return;
    dispatch(submitBatchAllocation(payloads))
      .unwrap()
      .then(() => onSuccess())
      .catch(() => {});
  };

  const columns: ColumnsType<VirtualWarehouseItem> = [
    { key: "sku", dataIndex: "sku", title: "SKU" },
    { key: "productName", dataIndex: "productName", title: "商品名稱" },
    {
      key: "expiryBatch",
      title: "效期_批號",
      render: (_: unknown, record: VirtualWarehouseItem) => {
        const { effectiveDate, batch } = record;
        if (!effectiveDate) return "-";
        return batch ? `${effectiveDate}_${batch}` : effectiveDate;
      },
    },
    {
      key: "retailerName",
      title: "來源通路",
      render: (_: unknown, record: VirtualWarehouseItem) =>
        record.stockId != null ? TRYALL_STOCK_CHANNEL_OPTION.label : record.retailerName,
    },
    {
      key: "destination",
      title: "配貨位置",
      render: () => destinationDisplayText,
    },
    {
      key: "qty",
      title: "配貨數量",
      render: (_, record) => {
        const key = getItemKey(record);
        const qty = qtyInputs[key];
        const isExceeded = qty !== undefined && qty > record.canUseQty;
        return (
          <div>
            <InputNumber
              min={1}
              value={qty}
              onChange={(v) => setQtyInputs((prev) => ({ ...prev, [key]: v ?? undefined }))}
            />
            {isExceeded && (
              <Typography.Text type="danger" style={{ display: "block", fontSize: 12 }}>
                配貨數量需小於當前可配數
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      key: "beforeQty",
      title: (
        <span>
          配貨前可分配數&nbsp;
          <Tooltip title="此商品在配貨位置，經配貨前的可分配數">
            <ExclamationCircleOutlined style={{ color: "#1890ff" }} />
          </Tooltip>
        </span>
      ),
      render: (_, record) => {
        if (retailerId === undefined) return 0;
        if (isFetchingDestination) return "...";
        return destinationAllocatableMap[`${retailerId}:${record.sku}`] ?? 0;
      },
    },
    {
      key: "afterQty",
      title: (
        <span>
          配貨後可分配數&nbsp;
          <Tooltip title="此商品在配貨位置，經配貨後的可分配數">
            <ExclamationCircleOutlined style={{ color: "#1890ff" }} />
          </Tooltip>
        </span>
      ),
      render: (_, record) => {
        if (retailerId === undefined) return 0;
        const before = destinationAllocatableMap[`${retailerId}:${record.sku}`] ?? 0;
        return before + (qtyInputs[getItemKey(record)] ?? 0);
      },
    },
  ];

  return (
    <Modal
      title="批量配貨"
      open
      onCancel={onClose}
      width={1300}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={isFetchingSubmit} disabled={isConfirmDisabled} onClick={handleConfirm}>
            確認
          </Button>
        </>
      }
    >
      {hasConflict && (
        <RedAlert message="通路不可與配貨位置相同" type="error" showIcon={false} style={{ marginBottom: 12 }} />
      )}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4 }}>配貨位置</div>
        <div style={{ display: "flex", gap: 8 }}>
          <Select
            placeholder="請選擇"
            style={{ width: 160 }}
            options={allocationChannelOptions}
            value={allocationTarget}
            onChange={handleAllocationChannelTypeChange}
          />
          {allocationTarget !== undefined && allocationTarget !== TRYALL_STOCK_CHANNEL_ID && (
            <Select
              showSearch
              allowClear
              placeholder="請選擇通路"
              style={{ width: 200 }}
              options={retailOptions}
              value={retailerId}
              filterOption={(input, option) =>
                typeof option?.label === "string" && option.label.toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleDealerChange}
            />
          )}
        </div>
      </div>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>當前選擇資料</div>
      <AllocateRowContext.Provider value={allocateRowContextValue}>
        <Table
          columns={columns}
          dataSource={selectedItems}
          rowKey={(record) => getItemKey(record)}
          pagination={false}
          size="small"
          scroll={{ x: "max-content" }}
          components={TABLE_COMPONENTS}
        />
      </AllocateRowContext.Provider>
    </Modal>
  );
}
